import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Chip,
  Alert,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/AccountBalance';
import CalculateIcon from '@mui/icons-material/Calculate';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { usePlayer } from '../../context/PlayerContext';
import { useExpense } from '../../context/ExpenseContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import CSVDownloadButton from '../../components/CSVDownloadButton/CSVDownloadButton';
import './Settlement.scss';

const Settlement = ({ sessionName = null }) => {
  const { players, getTotalBuyIns, getTotalCashOuts } = usePlayer();
  const { getTotalExpenses, getPlayerExpenseTotal, expenses } = useExpense();
  const [activeTab, setActiveTab] = useState('calculator');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculatePlayerBalance = (player) => {
    const totalBuyIn = player.totalBuyIn || 0;
    const totalCashOut = player.totalCashOut || 0;
    const linkedExpenses = getPlayerExpenseTotal(player.id);
    
    // Treat linked expenses as reimbursements - they ADD to the player's net balance
    const netBalance = (totalCashOut + linkedExpenses) - totalBuyIn;
    
    return {
      totalBuyIn,
      totalCashOut,
      linkedExpenses,
      netBalance,
      isWinner: netBalance > 0,
      isLoser: netBalance < 0
    };
  };

  const getPlayerBalances = () => {
    return players
      .filter(player => player.totalBuyIn > 0) // Only active players
      .map(player => ({
        ...player,
        balance: calculatePlayerBalance(player)
      }))
      .sort((a, b) => b.balance.netBalance - a.balance.netBalance);
  };

  const calculateSettlements = () => {
    const playerBalances = getPlayerBalances();
    const winners = playerBalances.filter(p => p.balance.isWinner);
    const losers = playerBalances.filter(p => p.balance.isLoser);
    
    const settlements = [];
    let winnerIndex = 0;
    let loserIndex = 0;
    
    while (winnerIndex < winners.length && loserIndex < losers.length) {
      const winner = winners[winnerIndex];
      const loser = losers[loserIndex];
      
      const amountToReceive = Math.abs(winner.balance.netBalance);
      const amountToPay = Math.abs(loser.balance.netBalance);
      
      const settlementAmount = Math.min(amountToReceive, amountToPay);
      
      if (settlementAmount > 0.01) { // Only show settlements > 1 cent
        settlements.push({
          from: loser,
          to: winner,
          amount: settlementAmount
        });
      }
      
      // Update remaining balances
      winner.balance.netBalance -= settlementAmount;
      loser.balance.netBalance += settlementAmount;
      
      // Move to next player if current one is settled
      if (Math.abs(winner.balance.netBalance) < 0.01) {
        winnerIndex++;
      }
      if (Math.abs(loser.balance.netBalance) < 0.01) {
        loserIndex++;
      }
    }
    
    return settlements;
  };

  const getTotalStats = () => {
    const totalBuyIns = getTotalBuyIns();
    const totalCashOuts = getTotalCashOuts();
    const totalExpenses = getTotalExpenses();
    const linkedExpensesTotal = expenses
      .filter(expense => expense.playerId)
      .reduce((total, expense) => total + expense.amount, 0);
    const unlinkedExpensesTotal = totalExpenses - linkedExpensesTotal;
    
    // Net amount calculation: Cash outs + linked expenses (reimbursements) - buy ins - unlinked expenses
    const netAmount = (totalCashOuts + linkedExpensesTotal) - totalBuyIns - unlinkedExpensesTotal;
    const activePlayersCount = players.filter(player => player.totalBuyIn > 0).length;
    
    return {
      totalBuyIns,
      totalCashOuts,
      totalExpenses,
      linkedExpensesTotal,
      unlinkedExpensesTotal,
      netAmount,
      activePlayersCount
    };
  };

  const getTotalNetBalance = () => {
    return playerBalances.reduce((total, player) => total + player.balance.netBalance, 0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const downloadSettlementCSV = () => {
    const playerBalances = getPlayerBalances();
    const settlements = calculateSettlements();
    
    const headers = [
      "Player Name",
      "Total Buy-ins",
      "Total Cash Outs", 
      "Reimbursements",
      "Net Balance",
      "Status"
    ];
    
    const balanceRows = playerBalances.map(player => [
      player.name,
      formatCurrency(player.balance.totalBuyIn),
      formatCurrency(player.balance.totalCashOut),
      formatCurrency(player.balance.linkedExpenses),
      formatCurrency(player.balance.netBalance),
      player.balance.isWinner ? "Winner" : player.balance.isLoser ? "Loser" : "Even"
    ]);
    
    const settlementHeaders = ["From Player", "To Player", "Amount"];
    const settlementRows = settlements.map(settlement => [
      settlement.from.name,
      settlement.to.name,
      formatCurrency(settlement.amount)
    ]);
    
    const csvData = [
      ["PLAYER BALANCES (Expenses treated as reimbursements)"],
      headers,
      ...balanceRows,
      [""],
      ["SETTLEMENT RECOMMENDATIONS"],
      settlementHeaders,
      ...settlementRows
    ];
    
    const csvContent = csvData.map(row => 
      row.map(field => {
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      }).join(",")
    ).join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const timestamp = new Date().toISOString().split('T')[0];
    const sessionSuffix = sessionName ? `_${sessionName.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
    link.setAttribute("download", `settlement_report${sessionSuffix}_${timestamp}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stats = getTotalStats();
  const playerBalances = getPlayerBalances();
  const settlements = calculateSettlements();
  const winners = playerBalances.filter(p => p.balance.isWinner);
  const losers = playerBalances.filter(p => p.balance.isLoser);

  return (
    <Box className="settlement-page fade-in">
      {/* Hero Section */}
      <Box className="settlement-hero">
        <Typography variant="h2" className="hero-title">
          Settlement Calculator
        </Typography>
        <Typography variant="h6" color="text.secondary" className="hero-subtitle">
          Calculate settlements between players with expense reimbursements
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} className="stats-section">
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card">
            <CardContent>
              <Box className="stat-content">
                <GroupIcon className="stat-icon" />
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {stats.activePlayersCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Players
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card highlight">
            <CardContent>
              <Box className="stat-content">
                <Box className="currency-icon blue">$</Box>
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(stats.totalBuyIns).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Buy-ins
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card success">
            <CardContent>
              <Box className="stat-content">
                <Box className="currency-icon green">$</Box>
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(stats.totalCashOuts).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cash Outs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card reimbursement">
            <CardContent>
              <Box className="stat-content">
                <ReceiptIcon className="stat-icon" />
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(stats.linkedExpensesTotal).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Reimbursements
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className={`stat-card ${stats.netAmount >= 0 ? 'positive' : 'negative'}`}>
            <CardContent>
              <Box className="stat-content">
                <BalanceIcon className="stat-icon" />
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(Math.abs(stats.netAmount)).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net Balance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expense Summary */}
      {stats.totalExpenses > 0 && (
        <Alert severity="info" className="expense-summary">
          <Typography variant="body1" className="summary-text">
            <strong>Expense Breakdown:</strong> 
            {formatCurrency(stats.linkedExpensesTotal)} reimbursements to players, 
            {formatCurrency(stats.unlinkedExpensesTotal)} general expenses
          </Typography>
        </Alert>
      )}

      {/* Content Tabs */}
      <Box className="content-section">
        <Box className="content-header">
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            className="content-tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab 
              label="Settlement Calculator"
              value="calculator"
              icon={<CalculateIcon />}
            />
            <Tab 
              label="Player Balances"
              value="balances"
              icon={<BalanceIcon />}
            />
            <Tab 
              label="Settlement Steps"
              value="steps"
              icon={<SwapHorizIcon />}
            />
          </Tabs>
          
          <CSVDownloadButton
            onClick={downloadSettlementCSV}
            disabled={playerBalances.length === 0}
            tooltip="Download settlement report as CSV"
            label="Export Report"
            className="export-button"
          />
        </Box>

        {/* Settlement Calculator Tab */}
        {activeTab === 'calculator' && (
          <Card className="calculator-card">
            <CardContent>
              <Typography variant="h5" className="card-title">
                Settlement Summary
              </Typography>
              
              <Grid container spacing={3} className="summary-grid">
                <Grid item xs={12} md={4}>
                  <Box className="summary-item winners">
                    <TrendingUpIcon className="summary-icon" />
                    <Typography variant="h6">Winners</Typography>
                    <Typography variant="h4" className="summary-count">
                      {winners.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total to receive: {formatCurrency(winners.reduce((sum, p) => sum + p.balance.netBalance, 0))}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box className="summary-item losers">
                    <TrendingDownIcon className="summary-icon" />
                    <Typography variant="h6">Losers</Typography>
                    <Typography variant="h4" className="summary-count">
                      {losers.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total to pay: {formatCurrency(Math.abs(losers.reduce((sum, p) => sum + p.balance.netBalance, 0)))}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box className="summary-item settlements">
                    <PaymentIcon className="summary-icon" />
                    <Typography variant="h6">Settlements</Typography>
                    <Typography variant="h4" className="summary-count">
                      {settlements.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total amount: {formatCurrency(settlements.reduce((sum, s) => sum + s.amount, 0))}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {settlements.length === 0 && playerBalances.length > 0 && (
                <Alert severity="success" className="no-settlements">
                  <Typography variant="body1">
                    🎉 All players are settled! No payments needed.
                  </Typography>
                </Alert>
              )}
              
              {playerBalances.length === 0 && (
                <Alert severity="info" className="no-players">
                  <Typography variant="body1">
                    No active players found. Add players with buy-ins to calculate settlements.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Player Balances Tab */}
        {activeTab === 'balances' && (
          <Card className="balances-card">
            <CardContent>
              <Typography variant="h5" className="card-title">
                Player Balance Details
              </Typography>
              
              <Alert severity="info" className="reimbursement-note">
                <Typography variant="body2">
                  <strong>Note:</strong> Linked expenses are treated as reimbursements and add to player balances.
                </Typography>
              </Alert>
              
              <TableContainer component={Paper} className="balances-table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell align="right">Buy-ins</TableCell>
                      <TableCell align="right">Cash Outs</TableCell>
                      <TableCell align="right">Reimbursements</TableCell>
                      <TableCell align="right">Net Balance</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {playerBalances.map((player) => {
                      const playerInitials = getInitials(player.name);
                      const backgroundColor = player.profileColor || "#006aff";
                      const textColor = getContrastTextColor(backgroundColor);
                      
                      return (
                        <TableRow key={player.id} className="balance-row">
                          <TableCell>
                            <Box className="player-info">
                              <Avatar 
                                className="player-avatar"
                                sx={{
                                  backgroundColor: backgroundColor,
                                  color: textColor,
                                  fontWeight: 700,
                                  width: 32,
                                  height: 32,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {playerInitials}
                              </Avatar>
                              <Typography variant="body1" className="player-name">
                                {player.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" className="amount">
                              {formatCurrency(player.balance.totalBuyIn)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" className="amount">
                              {formatCurrency(player.balance.totalCashOut)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {player.balance.linkedExpenses > 0 ? (
                              <Typography variant="body2" className="reimbursement-amount">
                                +{formatCurrency(player.balance.linkedExpenses)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body1" 
                              className={`net-balance ${player.balance.isWinner ? 'winner' : player.balance.isLoser ? 'loser' : 'even'}`}
                            >
                              {formatCurrency(player.balance.netBalance)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={
                                player.balance.isWinner ? "Winner" : 
                                player.balance.isLoser ? "Loser" : "Even"
                              }
                              color={
                                player.balance.isWinner ? "success" : 
                                player.balance.isLoser ? "error" : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="total-row">
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: getTotalNetBalance() >= 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {formatCurrency(getTotalNetBalance())}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">-</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Settlement Steps Tab */}
        {activeTab === 'steps' && (
          <Card className="steps-card">
            <CardContent>
              <Typography variant="h5" className="card-title">
                Settlement Instructions
              </Typography>
              
              {settlements.length > 0 ? (
                <List className="settlement-list">
                  {settlements.map((settlement, index) => {
                    const fromInitials = getInitials(settlement.from.name);
                    const toInitials = getInitials(settlement.to.name);
                    const fromColor = settlement.from.profileColor || "#006aff";
                    const toColor = settlement.to.profileColor || "#006aff";
                    
                    return (
                      <React.Fragment key={index}>
                        <ListItem className="settlement-item">
                          <ListItemIcon>
                            <Box className="step-number">{index + 1}</Box>
                          </ListItemIcon>
                          <ListItemText>
                            <Box className="settlement-details">
                              <Box className="players">
                                <Box className="player from">
                                  <Avatar 
                                    sx={{
                                      backgroundColor: fromColor,
                                      color: getContrastTextColor(fromColor),
                                      width: 40,
                                      height: 40,
                                      fontWeight: 700
                                    }}
                                  >
                                    {fromInitials}
                                  </Avatar>
                                  <Typography variant="body1">{settlement.from.name}</Typography>
                                </Box>
                                
                                <Box className="arrow">
                                  <SwapHorizIcon />
                                  <Typography variant="h6" className="amount">
                                    {formatCurrency(settlement.amount)}
                                  </Typography>
                                </Box>
                                
                                <Box className="player to">
                                  <Avatar 
                                    sx={{
                                      backgroundColor: toColor,
                                      color: getContrastTextColor(toColor),
                                      width: 40,
                                      height: 40,
                                      fontWeight: 700
                                    }}
                                  >
                                    {toInitials}
                                  </Avatar>
                                  <Typography variant="body1">{settlement.to.name}</Typography>
                                </Box>
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary" className="instruction">
                                {settlement.from.name} pays {formatCurrency(settlement.amount)} to {settlement.to.name}
                              </Typography>
                            </Box>
                          </ListItemText>
                        </ListItem>
                        {index < settlements.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <Alert severity="success" className="no-settlements">
                  <Typography variant="body1">
                    🎉 No settlements needed! All players are even or there are no active players.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default Settlement;